import { RegionShared } from "@1games-ru/region-shared";
import { motion } from "motion/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, CrossIcon, Mask2Icon } from "@/shared/icons";
import TargetMenuChildren from "@/domains/targetMenu/components/TargetMenuChildren";
import {
  CIRCLE_CENTER_X,
  CIRCLE_CENTER_Y,
  CIRCLE_GAP,
  CIRCLE_INNER_RADIUS,
  INNER_MENU_SIZE,
} from "@/domains/targetMenu/constants/TargetMenuCircle";
import describeSector from "@/domains/targetMenu/helpers/describeSector";
import iconDict from "@/domains/targetMenu/helpers/iconDict";

type Props = {
  items: Array<typeof RegionShared.Types.TargetMenu.MenuItem>;
  onClose: () => void;
  onClick: (item: typeof RegionShared.Types.TargetMenu.MenuItem) => void;
  onChangeItems: (value: (typeof RegionShared.Types.TargetMenu.MenuItem)[] | null) => void;
  isNestedItems?: boolean;
};

export default function TargetMenu({
  items,
  isNestedItems,
  onChangeItems,
  onClose,
  onClick,
}: Props) {
  const { t } = useTranslation("targetMenu");
  // заполнение visibleItems до 6 секторов
  const fullItems = useMemo(() => {
    const result = [...items];
    while (result.length < 6) {
      result.push(null);
    }

    return result;
  }, [items]);

  const closeButtonHandler = useCallback(isNestedItems ? () => onChangeItems(null) : onClose, [
    isNestedItems,
    onClose,
  ]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 2 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="circular-menu"
      style={{ "--menu-size": `${INNER_MENU_SIZE / 16}rem` } as React.CSSProperties}
    >
      <button type="button" className="circular-menu-close group" onClick={closeButtonHandler}>
        {isNestedItems ? <ArrowLeftIcon size={20} /> : <CrossIcon size={20} />}

        <div className="text-16 font-bold invisible opacity-0 h-0 group-hover:opacity-100 group-hover:visible group-hover:h-fit group-hover:mt-2 overflow-hidden transition-all duration-100">
          {isNestedItems ? "Назад" : "Выход"}
        </div>
      </button>
      <div className="circular-menu-inner">
        {fullItems.map((item, index) => {
          const outerRadius = CIRCLE_CENTER_X;
          const totalSectors = fullItems.length;
          const sectorSize = 360 / totalSectors;

          const startAngle = (index / totalSectors) * 360 - sectorSize;
          const endAngle = ((index + 1) / totalSectors) * 360 - sectorSize;

          const commonData = {
            x: CIRCLE_CENTER_X,
            y: CIRCLE_CENTER_Y,
            startAngle,
            endAngle,
          };

          // разметка секторов
          const shapePath = describeSector({
            ...commonData,
            r: CIRCLE_INNER_RADIUS - CIRCLE_GAP,
            r2: outerRadius,
            gap: 0.001,
          });

          // видимая область сектора
          const fillPath = describeSector({
            ...commonData,
            r: CIRCLE_INNER_RADIUS - CIRCLE_GAP,
            r2: outerRadius - CIRCLE_GAP,
            gap: CIRCLE_GAP / 2,
          });

          // внутренний сектор для градиента белого или красного
          const innerSectorPath = describeSector({
            ...commonData,
            r: CIRCLE_INNER_RADIUS + CIRCLE_GAP,
            r2: outerRadius - 1.75 * CIRCLE_GAP,
            gap: CIRCLE_GAP * 0.9,
          });

          // красная линия при наведении
          const strokePath = describeSector({
            ...commonData,
            r: outerRadius - 3 * CIRCLE_GAP,
            r2: outerRadius - 1.75 * CIRCLE_GAP,
            gap: CIRCLE_GAP * 0.33,
          });

          const angle = ((index + 0.5) / totalSectors) * 360 - 90 - sectorSize;

          if (item) {
            const Icon =
              (item.eventName && iconDict[item.eventName]
                ? iconDict[item.eventName]
                : iconDict[item.name]) || Mask2Icon;

            return (
              <div
                className="circular-menu-group"
                key={item.name}
                // data-active={activeItemId === item.id}
              >
                {item.items && isNestedItems && (
                  <TargetMenuChildren
                    onClick={(itemValue) => {
                      onClick(itemValue);
                    }}
                    parentSize={360 / totalSectors}
                    parentStartAngle={startAngle}
                    items={item.items}
                  />
                )}
                <motion.div
                  initial={{
                    opacity: 0.7,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.1,
                    delay: 0.05 * index,
                  }}
                  exit={{ opacity: 0 }}
                  className="circular-menu-sector circular-menu-sector--parent"
                  // eslint-disable-next-line
                  // data-active={activeItemId === item.id}
                  data-inert={item.name ? null : ""}
                  style={
                    {
                      "--inner-sector-path": `path('${innerSectorPath}')`,
                      "--angle-sin": `${Math.sin(angle * (Math.PI / 180))}`,
                      "--angle-cos": `${Math.cos(angle * (Math.PI / 180))}`,
                      "--shape-path": `path('${shapePath}')`,
                      "--fill-path": `path('${fillPath}')`,
                      "--stroke-path": `path('${strokePath}')`,
                    } as React.CSSProperties
                  }
                >
                  <button
                    type="button"
                    className="circular-menu-sector-btn"
                    onClick={() => {
                      // если находимся на вложенном уровне (2 уровне) и нет вложенного уровня (3 уровня)
                      if (isNestedItems) {
                        if (!item.items) {
                          onClick(item);
                        }
                        return;
                      }

                      // если на 1 уровне и есть вложенный уровень (2 уровень)
                      if (item.items) {
                        onChangeItems(item.items);
                      } else {
                        // если на первом уровне и нет вложенного уровня (2 уровня)
                        onClick(item);
                      }
                    }}
                  >
                    <span className="circular-menu-sector-btn-inner">
                      <Icon />
                      <span className="capitalize">{t(`targetMenu.${item.name}`)}</span>
                    </span>
                  </button>
                </motion.div>
              </div>
            );
          }

          return (
            <div className="circular-menu-group" key={-index}>
              <div
                className="circular-menu-sector"
                data-disabled="true"
                style={
                  {
                    "--shape-path": `path('${shapePath}')`,
                    "--fill-path": `path('${fillPath}')`,
                  } as React.CSSProperties
                }
              />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
